import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type BrandAccount as BrandAccountBase } from '~/gql/graphql'

type BrandAccount = App.PickGraphQL<BrandAccountBase, 'id' | 'name'>

export type BrandAccountSelectorProps<TBrandAccount extends BrandAccount> =
  Omit<
    ItemSelectorProps,
    'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
  > & {
    brandAccounts: TBrandAccount[]
    onChange?: (brandAccountId: TBrandAccount['id']) => void
    value: TBrandAccount['id'] | null
  }

export const BrandAccountSelector = React.forwardRef(
  <TBrandAccount extends BrandAccount>(
    {
      brandAccounts,
      onChange,
      value: valueProp,
      ...rest
    }: BrandAccountSelectorProps<TBrandAccount>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const value = React.useMemo(
      () => brandAccounts.find(({ id }) => id === valueProp),
      [brandAccounts, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (brandAccount: TBrandAccount) => {
        onChange?.(brandAccount.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={brandAccounts}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
